/** The panel that answers "what is this?" for whatever was clicked.
 *
 *  Deliberately a READER, not an editor. The plan is generated, and a value
 *  typed over a generated one is a lie the next solve erases — so every row is
 *  text. What it adds is the half of the drawing that was never on the sheet:
 *  the norm each object answers to, and the object's own measurement beside the
 *  number that norm requires. A dwelling at 51.2 m² where anexa 1 asks 52.0
 *  looks exactly like a compliant one on the drawing; here the two numbers sit
 *  on adjacent lines and the second is flagged.
 */
import type React from "react";
import type { Described, Figure, Selection } from "./inspect";
import { useT } from "../i18n";

/** The object's own outline, over the rings around it.
 *
 *  The rows can say "Colțuri: 7 (formă în L)" but not WHICH corner is missing,
 *  and on a flat that wrapped a stair core the notch is the whole story. Drawn
 *  from the same metre-space ring the plan draws, so this cannot drift from the
 *  sheet the way a redrawn thumbnail would.
 */
function FigureView({ fig }: { fig: Figure }) {
  const ok = (r: number[][] | undefined): r is number[][] => !!r && r.length >= 3;
  const ctx = (fig.context ?? []).filter(ok);
  if (!ok(fig.poly)) return null;

  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const ring of [fig.poly, ...ctx]) {
    for (const [x, y] of ring) {
      x0 = Math.min(x0, x); x1 = Math.max(x1, x);
      y0 = Math.min(y0, y); y1 = Math.max(y1, y);
    }
  }
  // A ring collapsed to a line has zero extent on one axis, which would make
  // the viewBox degenerate and blank the whole box rather than draw a sliver.
  const w = Math.max(x1 - x0, 0.01), h = Math.max(y1 - y0, 0.01);
  const pad = Math.max(w, h) * 0.06;

  const d = (r: number[][]) =>
    r.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(3)},${p[1].toFixed(3)}`)
      .join(" ") + " Z";

  return (
    <svg
      className="pg-inspect-fig"
      viewBox={`${x0 - pad} ${y0 - pad} ${w + 2 * pad} ${h + 2 * pad}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Forma obiectului selectat"
    >
      {/* The viewBox is in METRES, so an ordinary stroke-width of 1 would be a
          one-metre-thick line — invisible on a 60 m bar and covering a 2.5 m
          stall entirely. Widths are pinned to device pixels instead. */}
      {ctx.map((r, i) => (
        <path key={i} d={d(r)} className="pg-fig-ctx" vectorEffect="non-scaling-stroke" />
      ))}
      <path d={d(fig.poly)} className="pg-fig-main" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

export default function Inspector(
  { info, onClose, onSelect, extra }: {
    info: Described;
    onClose: () => void;
    onSelect: (s: Selection) => void;
    /** Un panou în plus, sub rânduri — azi variantele de recompartimentare ale unui
     *  apartament (`RecompPanou`), singurul loc din inspector care face ceva. */
    extra?: React.ReactNode;
  },
) {
  const t = useT();
  const up = info.parent;
  return (
    <div className="pg-inspect">
      <div className="pg-inspect-head">
        <div>
          <div className="pg-inspect-title">{info.title}</div>
          <div className="pg-inspect-chips">
            {info.chips.map((c, i) => (
              <span key={i} className="pg-pill">{c}</span>
            ))}
          </div>
        </div>
        <button className="pg-inspect-x" onClick={onClose} title={t("insp.inchide")}>
          ×
        </button>
      </div>

      <div className="pg-inspect-body">
        {info.figure && <FigureView fig={info.figure} />}

        {/* The only way to reach the whole dwelling. A press resolves to the
            innermost drawn thing, so a flat — which is covered edge to edge by
            its own rooms — can never win a click on its own. */}
        {up && (
          <button
            type="button"
            className="pg-inspect-up"
            onClick={() => onSelect(up.sel)}
            title={t("insp.sus")}
          >
            <span aria-hidden="true">↑</span> {up.label}
          </button>
        )}

        {/* A group with no rows would print its heading over nothing — which
            happens honestly, e.g. "Formă" on an object whose polygon did not
            survive the lot intersection. */}
        {info.groups.filter((g) => g.rows.length > 0).map((g, i) => (
          <section key={i}>
            <h4>{g.label}</h4>
            <dl>
              {g.rows.map((r, j) => (
                <div key={j} className={r.warn ? "pg-inspect-warn" : undefined}>
                  <dt>{r.k}</dt>
                  <dd>{r.v}</dd>
                </div>
              ))}
            </dl>
          </section>
        ))}

        {extra}

        {/* The cited-norm list is parked, not removed: describeSelection still
            attaches `rules` to every object and RULES still holds the quoted
            norms, so this comes back by restoring this block alone. */}
      </div>
    </div>
  );
}
